import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Card, Checkbox, Input, Select, Space, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined } from '@ant-design/icons';
import { COMPETENCY_PERMS as P, useAssessmentCycles, useCompetencyAssessments, useCompetencyCan } from '@/hooks/useCompetency';
import type { CompetencyAssessment } from '@/services/competency.service';
import { formatDate } from '@/utils/format/date';
import CompetencyNav from './CompetencyNav';
import { ASSESSMENT_TYPES, COMPETENCY_BASE, GRADE_COLOR, GRADE_LABEL, STATUS_COLOR } from './competencyDisplay';

const AssessmentsPage: React.FC = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const seesAll = useCompetencyCan(P.assessmentView, P.assessmentClose, P.assessmentEvaluate);
  const canViewCycles = useCompetencyCan(P.assessmentView, P.assessmentClose);
  const [status, setStatus] = useState<string | undefined>(params.get('status') ?? undefined);
  const [type, setType] = useState<string>();
  const [cycleId, setCycleId] = useState<string | undefined>(params.get('cycle_id') ?? undefined);
  const [mine, setMine] = useState(false);
  const [search, setSearch] = useState('');
  const { data = [], isLoading } = useCompetencyAssessments({ status, type, cycle_id: cycleId, mine: mine ? '1' : undefined, search });
  const { data: cycles = [] } = useAssessmentCycles(canViewCycles);

  const columns: ColumnsType<CompetencyAssessment> = [
    {
      title: 'Assessment',
      dataIndex: 'assessment_number',
      width: 150,
      render: (v, r) => <a onClick={() => navigate(`${COMPETENCY_BASE}/assessments/${r.id}`)}>{v}</a>,
    },
    { title: 'Employee', render: (_, r) => [r.employee_number, r.employee_name].filter(Boolean).join(' - ') },
    { title: 'Designation', dataIndex: 'job_title', ellipsis: true },
    { title: 'Department', dataIndex: 'department_name', ellipsis: true },
    { title: 'Type', width: 170, render: (_, r) => (r.assessment_type === 'Other' ? r.assessment_type_other || 'Other' : r.assessment_type) },
    { title: 'Date', dataIndex: 'assessment_date', width: 110, render: (v) => formatDate(v) },
    { title: 'Evaluator', dataIndex: 'evaluator_name', width: 160 },
    {
      title: 'Grade',
      width: 150,
      render: (_, r) => (r.grade ? <Tag color={GRADE_COLOR[r.grade]}>{`${r.grade} (${r.total_score}) ${GRADE_LABEL[r.grade]}`}</Tag> : '-'),
    },
    { title: 'Recommendation', dataIndex: 'recommendation', width: 150 },
    {
      title: 'Status',
      width: 170,
      render: (_, r) => (
        <Space size={4}>
          <Tag color={STATUS_COLOR[r.status]}>{r.status}</Tag>
          {r.hr_outcome && <Tag>{r.hr_outcome}</Tag>}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <CompetencyNav
        title="Competency assessments"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate(`${COMPETENCY_BASE}/assessments/new`)}>
            New assessment
          </Button>
        }
      />
      <Card>
        <Space wrap style={{ marginBottom: 12 }}>
          <Input.Search placeholder="Search number or employee" allowClear style={{ width: 260 }} onSearch={setSearch} />
          <Select allowClear placeholder="Status" style={{ width: 140 }} value={status} onChange={setStatus} options={['Draft', 'Submitted', 'Closed'].map((s) => ({ value: s, label: s }))} />
          <Select allowClear placeholder="Type" style={{ width: 200 }} value={type} onChange={setType} options={ASSESSMENT_TYPES.map((s) => ({ value: s, label: s }))} />
          {canViewCycles && (
            <Select
              allowClear
              placeholder="Yearly cycle"
              style={{ width: 150 }}
              value={cycleId}
              onChange={setCycleId}
              options={cycles.map((c) => ({ value: c.id, label: String(c.cycle_year) }))}
            />
          )}
          {seesAll && (
            <Checkbox checked={mine} onChange={(e) => setMine(e.target.checked)}>
              My team only
            </Checkbox>
          )}
        </Space>
        <Table rowKey="id" size="small" loading={isLoading} dataSource={data} columns={columns} pagination={{ defaultPageSize: 20 }} scroll={{ x: 1300 }} />
      </Card>
    </div>
  );
};

export default AssessmentsPage;
